import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, forkJoin, merge, of, timer } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    groupBy,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { AumicoAbcpPollIntervals, AumicoAbcpPollIntervalsToken } from '../abcp-data.config';
import { closingDocumentActions } from '../actions/closing-document.actions';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import { DocumentFilterKey, DocumentSort } from '../components/documents-hub/documents-hub.types';
import { IClosingDocumentDetail, IUploadFailure } from '../models/closing-document.model';
import { isAnyDocumentAiWorking } from '../presenters/document-ai-state';
import {
    selectAccountDocumentsRequest,
    selectAccountsStatus,
    selectActiveFilter,
    selectAgentOnly,
    selectDocumentDetail,
    selectDocumentSort,
    selectPendingUploadFilenames,
    selectProjectId,
    selectSearchTerm,
    selectSyncPollTick,
    selectUploadPollTick,
} from '../selectors/closing-document.selectors';
import { ClosingDocumentService } from '../services/closing-document.service';

/** HTTP status the ABCP route answers for a non-PDF (ADR-0009 §12). */
const HTTP_UNSUPPORTED_MEDIA_TYPE = 415;
const HTTP_PAYLOAD_TOO_LARGE = 413;

/**
 * How long `setSearchTerm` has to go quiet before the list is re-read.
 *
 * A search is a SERVER query here — it recomputes the three counts over the whole closing — so one
 * request per dispatch would be one closing-wide count per keystroke.
 *
 * ⚠️ This is a SAFETY NET, not the main throttle, which is why it is short. The field that feeds it
 * today (`ham-filter` with `[autoEmit]="true"` in the workspace header) already debounces by
 * `USER_INPUT_DEBOUNCE_TIME` = 500 ms, so the observed delay is the SUM. The slice keeps its own
 * window regardless: `setSearchTerm` is a public facade method, and the slice must not depend on
 * every future caller having a debounce of its own.
 */
const SEARCH_RELOAD_DEBOUNCE_MS = 250;

/**
 * REV-22302 — how often the list is re-read while the AI is still working on a row. Three seconds:
 * the row says "KI liest den Beleg" and the trustee is watching it, so the outcome should land
 * within a glance; and it is above the override floor (`AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS`).
 */
export const AI_WORKING_POLL_MS = 3_000;

@Injectable()
export class ClosingDocumentEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly service: ClosingDocumentService,
        @Inject(AumicoAbcpPollIntervalsToken) private readonly intervals: AumicoAbcpPollIntervals,
    ) {}

    /**
     * REV-21901 N3 (W1/W3) — the slice's boolean as the wire's value. ONE place folds the two, so no
     * reload site can invent a third spelling: `true` is `'agent'` (this client's only facet), and
     * `false` is `null` — which `listDocuments` OMITS from the request, like `search`/`sort`.
     */
    private static toAssignedBy(agentOnly: boolean): 'agent' | null {
        return agentOnly ? 'agent' : null;
    }

    /**
     * `switchMap`: a fast second pill click must CANCEL the first request, not race it. With
     * `mergeMap` a slow response for `inbox` could land after a fast one for `assigned` and leave the
     * table showing rows the active pill does not describe.
     */
    public readonly loadDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadDocuments),
            switchMap(({ projectId, filter, search, sort, assignedBy }) =>
                this.service.listDocuments(projectId, filter, search, sort, assignedBy).pipe(
                    map((response) => closingDocumentActions.loadDocumentsSuccess({ response })),
                    catchError(() => of(closingDocumentActions.loadDocumentsFailure())),
                ),
            ),
        ),
    );

    public readonly loadDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadDocument),
            switchMap(({ projectId, documentId }) =>
                this.service.getDocument(projectId, documentId).pipe(
                    map((document) => closingDocumentActions.loadDocumentSuccess({ document })),
                    catchError(() => of(closingDocumentActions.loadDocumentFailure())),
                ),
            ),
        ),
    );

    /**
     * Upload, then reload.
     *
     * Per-file requests run CONCURRENTLY but settle TOGETHER (`forkJoin`), because the modal's
     * contract is one settle with the complete failure list — the user must not watch it close while
     * two files are still in flight.
     *
     * Every per-file stream is caught INSIDE the `forkJoin`, so one rejected file cannot collapse the
     * whole batch: `forkJoin` errors as soon as any source errors, which would discard the results of
     * the files that succeeded. That is exactly the partial-success case ADR-0009 §11 describes.
     */
    public readonly uploadDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.uploadDocuments),
            mergeMap(({ projectId, files, accountId }) =>
                forkJoin(
                    files.map((file) =>
                        this.service.uploadDocument(projectId, file, accountId).pipe(
                            map(() => ({ filename: file.name, failure: null as IUploadFailure | null })),
                            catchError((error: unknown) =>
                                of({
                                    filename: file.name,
                                    failure: {
                                        filename: file.name,
                                        reason: ClosingDocumentEffects.toFailureReason(error),
                                    } as IUploadFailure,
                                }),
                            ),
                        ),
                    ),
                ).pipe(
                    withLatestFrom(this.store.select(selectAccountDocumentsRequest)),
                    mergeMap(([results, requested]) => {
                        const succeededFilenames = results
                            .filter((result) => result.failure == null)
                            .map((result) => result.filename);
                        const settled = closingDocumentActions.uploadDocumentsSettled({
                            failures: results
                                .map((result) => result.failure)
                                .filter((failure): failure is IUploadFailure => failure != null),
                            // #162 (AP8a) — exactly the files that landed, so `pollAfterUpload$` watches
                            // the right rows and not one the server rejected before it ever existed.
                            succeededFilenames,
                        });
                        if (accountId == null) {
                            return [settled];
                        }

                        // Filed to an account on upload: that account's list is the one on screen, and
                        // its reconciliation is what changes — `reloadAfterUpload$` only knows the hub.
                        // ⚠️ Only while that account is STILL the one asked for. An upload that settles
                        // after the user opened another account (or closing) would otherwise cancel that
                        // account's read and show this account's documents under its header.
                        const stillOnScreen = requested?.projectId === projectId && requested.accountId === accountId;
                        return [
                            settled,
                            ...(stillOnScreen
                                ? [closingDocumentActions.loadAccountDocuments({ projectId, accountId })]
                                : []),
                            ...(succeededFilenames.length > 0
                                ? [projectAccountsActions.watchReconciliation({ projectId, accountIds: [accountId] })]
                                : []),
                        ];
                    }),
                ),
            ),
        ),
    );

    /**
     * After an upload settles, reload the list — the new documents have to appear, and the COUNTS
     * have to move (they are server-side, so nothing local can update them).
     *
     * Reloads even when every file failed: the failures may be per-file 415s while the closing changed
     * for another reason, and a reload is cheap next to showing a stale table.
     *
     * `projectId`, `filter` and `search` all come from the STORE rather than being threaded through
     * the settle action — the slice already knows what is on screen, and an effect instance field
     * would survive a route change and reload the wrong project.
     */
    public readonly reloadAfterUpload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.uploadDocumentsSettled),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([, projectId, filter, search, sort, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * The post-upload poll (#162, AP8a, planned case 4) — turns the ONE reload `reloadAfterUpload$`
     * already does into a bounded chain, so the row picks up a proposal that lands minutes later
     * WITHOUT the trustee reloading the hub.
     *
     * Watches `state.pendingUploadFilenames`, set by `uploadDocumentsSettled` to exactly the files this
     * batch's upload accepted. Rides every `Load Documents Success` — not only the ones this poll itself
     * scheduled — so a pill click or a search that happens to run while a proposal is still pending also
     * re-evaluates the watch-list against fresh rows, instead of the poll silently going stale.
     *
     * `switchMap` on the success stream: a NEWER answer always supersedes whatever timer the OLDER one
     * scheduled, so two overlapping list reads can never produce two ticking chains.
     *
     * Ends when NONE of the watched filenames are still `open` any more (a proposal, a manual
     * assignment or "nicht relevant" all count as settled — the row has left `open` either way), or at
     * the tick ceiling (`AumicoAbcpPollIntervalsToken`, the same shared cadence `pollReconciliation$`
     * uses) — the same "quiet failure, bounded retry" shape spec:GH-162 S5 asks of the reconciliation
     * poll, applied here so a broken list route cannot hammer it forever either.
     */
    public readonly pollAfterUpload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadDocumentsSuccess),
            withLatestFrom(
                this.store.select(selectPendingUploadFilenames),
                this.store.select(selectUploadPollTick),
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            filter(([, pendingFilenames]) => pendingFilenames.length > 0),
            switchMap(([{ response }, pendingFilenames, tick, projectId, filterKey, search, sort, agentOnly]) => {
                const stillOpen = response.documents.some(
                    (document) => pendingFilenames.includes(document.filename) && document.assignment === 'open',
                );

                if (!stillOpen || projectId == null || tick >= this.intervals.maxTicks) {
                    return of(closingDocumentActions.uploadPollSettled());
                }

                return timer(this.intervals.slowMs).pipe(
                    map(() =>
                        closingDocumentActions.loadDocuments({
                            projectId,
                            filter: filterKey,
                            search,
                            sort,
                            assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                        }),
                    ),
                );
            }),
        ),
    );

    /**
     * REV-22302 — keep the list fresh while the AI is working on a row.
     *
     * The row says "KI liest den Beleg" from its own fields (`documentAiStateOf`), and the SAME function
     * judges the wire response here: while any document is still being worked on, the list is re-read
     * every few seconds, so the proposal (or the refusal) lands on the row without the trustee reloading.
     * The reload is the quiet kind — `Load Documents` keeps the rows, the open picker is the view's own
     * state, and `trackBy` keeps the row elements — the same shape as the post-upload poll.
     *
     * Bounded WITHOUT a tick counter: the working claim itself expires `AI_WORKING_WINDOW_MS` after a
     * document's arrival, so a row the pipeline never picks up stops being asked about on its own.
     * `switchMap`: a newer answer supersedes the timer the older one scheduled — one chain, never two.
     * Leaving the tab (`Clear Document`, dispatched by the page on destroy) drops the pending tick.
     */
    public readonly pollWhileAiWorking$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadDocumentsSuccess),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            switchMap(([{ response }, projectId, filterKey, search, sort, agentOnly]) =>
                projectId == null || !isAnyDocumentAiWorking(response.documents, Date.now())
                    ? EMPTY
                    : timer(AI_WORKING_POLL_MS).pipe(
                          takeUntil(this.actions$.pipe(ofType(closingDocumentActions.clearDocument))),
                          map(() =>
                              closingDocumentActions.loadDocuments({
                                  projectId,
                                  filter: filterKey,
                                  search,
                                  sort,
                                  assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                              }),
                          ),
                      ),
            ),
        ),
    );

    /**
     * The reversible flag, then a reload — again because the counts move between pills and only the
     * server knows the new numbers. An account page of this closing re-reads its documents and the
     * account too, AFTER the write: the document leaves its list and the account may reopen.
     */
    public readonly setNotRelevant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setNotRelevant),
            withLatestFrom(
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
                this.store.select(selectAccountDocumentsRequest),
                this.store.select(selectDocumentDetail),
            ),
            mergeMap(([{ projectId, documentId, notRelevant }, filter, search, sort, agentOnly, requested, open]) =>
                this.service.setNotRelevant(projectId, documentId, notRelevant).pipe(
                    mergeMap((document) => [
                        // The document page shows the answer at once (taking the mark back
                        // stays on the page). Only for the open document: the list sends this too.
                        ...(open?.id === documentId ? [closingDocumentActions.loadDocumentSuccess({ document })] : []),
                        closingDocumentActions.loadDocuments({
                            projectId,
                            filter,
                            search,
                            sort,
                            assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                        }),
                        ...(requested?.projectId === projectId
                            ? [
                                  closingDocumentActions.loadAccountDocuments(requested),
                                  projectAccountsActions.reloadAccounts({ projectId }),
                              ]
                            : []),
                        closingDocumentActions.rowWriteSucceeded({ documentId }),
                    ]),
                    catchError(() =>
                        of(
                            closingDocumentActions.rowWriteFailed({ documentId }),
                            closingDocumentActions.loadDocumentsFailure(),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * "Re-ask for what is on screen" — the entry point for every caller that knows the closing but
     * not the pill or the search term (the tab page on mount, the SharePoint settings modal closing).
     *
     * This is the ONE place that reads `filter` and `search` out of the slice. Every other load
     * carries them on the action, so a forgotten one is a compile error rather than a pill that
     * quietly jumps back to Eingang or counts that quietly un-narrow.
     */
    public readonly reloadDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.reloadDocuments),
            withLatestFrom(
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                // The facet too (W2): this is the remount/modal path, and it must re-ask for exactly
                // what is on screen — rows without the facet under a toggle showing "an" is precisely
                // what moving the flag into the slice exists to prevent.
                this.store.select(selectAgentOnly),
            ),
            map(([{ projectId }, filter, search, sort, agentOnly]) =>
                closingDocumentActions.loadDocuments({
                    projectId,
                    filter,
                    search,
                    sort,
                    assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                }),
            ),
        ),
    );

    /**
     * Pull from SharePoint, then reload — the import creates rows and moves the server-side counts, so
     * the hub cannot show the result without re-reading.
     *
     * `exhaustMap`, not `switchMap`: the pull has SIDE EFFECTS on the server (it downloads files and
     * creates documents), so a second click must be IGNORED while one is in flight rather than
     * cancelling it. Cancelling the HTTP would not cancel the import that is already running server-side
     * — it would only hide it, and the user would see "nothing happened" while documents appeared
     * behind their back.
     */
    public readonly syncSharepoint$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.syncSharepoint),
            exhaustMap(({ projectId }) =>
                this.service.fetchSharepointFiles(projectId).pipe(
                    map((result) => closingDocumentActions.syncSharepointSuccess({ result })),
                    catchError(() => of(closingDocumentActions.syncSharepointFailure())),
                ),
            ),
        ),
    );

    // --- REV-21964 - R3/U1: start, follow, report --------------------------------------------------
    //
    // The chain is four effects and not one, because each answers a different question and they have
    // different lifetimes: starting is a WRITE (exhaustMap), following is a decision about the answer,
    // reading a report is a READ (switchMap), and the poll is a self-scheduling chain with a ceiling.
    // Fusing them would tie the write's back-pressure to the read's cancellation.

    /**
     * Start a run and come back at once with its id (R3).
     *
     * `exhaustMap` for the same reason the synchronous pull uses it: the request has SIDE EFFECTS on
     * the server, so a second click must be IGNORED while one is in flight rather than cancelling it.
     * Cancelling the HTTP would not cancel a run that is already persisted - it would only hide it.
     *
     * A REFUSAL travels through the success path on purpose. All three are HTTP 200 carrying an
     * answer; routing them through `...Failure` would render them as "something broke" and invite the
     * one action that is wrong in every case, which is pressing the button again.
     */
    public readonly startSharepointSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.startSharepointSync),
            exhaustMap(({ projectId }) =>
                this.service.startSharepointSync(projectId).pipe(
                    map((result) => closingDocumentActions.startSharepointSyncResult({ result })),
                    catchError(() => of(closingDocumentActions.startSharepointSyncFailure())),
                ),
            ),
        ),
    );

    /**
     * Follow whatever run the start answered with - INCLUDING the one `alreadyRunning` names.
     *
     * That is the whole reason the refusal carries an id: the user asked for a synchronisation, one is
     * already doing exactly that, and the honest answer is the running run's report rather than a
     * notice about a click that changed nothing.
     */
    public readonly followSyncRun$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.startSharepointSyncResult),
            withLatestFrom(this.store.select(selectProjectId)),
            mergeMap(([{ result }, projectId]) =>
                result.runId == null || projectId == null
                    ? []
                    : [closingDocumentActions.loadSyncRun({ projectId, runId: result.runId })],
            ),
        ),
    );

    /**
     * Read ONE run's report, or the newest when no id is known (`runId: null`) - which is how the hub
     * shows the last result on open without holding an id across a navigation.
     *
     * ⚠️ Reading a report is OUR OWN row, not a call to Microsoft. It does not breach "the tab never
     * pulls on load": nothing here walks a folder or downloads a file.
     */
    public readonly loadSyncRun$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadSyncRun),
            switchMap(({ projectId, runId }) =>
                (runId == null
                    ? this.service.getLatestSyncRun(projectId)
                    : this.service.getSyncRun(projectId, runId)
                ).pipe(
                    map((run) => closingDocumentActions.loadSyncRunSuccess({ run })),
                    catchError(() => of(closingDocumentActions.loadSyncRunFailure())),
                ),
            ),
        ),
    );

    /**
     * Keep asking while the run is still going, and stop for good when it is not.
     *
     * The same bounded, self-scheduling shape as `pollAfterUpload$` and for the same reason
     * (#162/SRR F3): a run that hangs server-side must not leave the tab asking until the user
     * navigates away. The ceiling is in TICKS, so a shortened cadence in a test does not also shorten
     * the number of attempts being tested.
     *
     * Hitting the ceiling ends the WATCHING, not the run. The report keeps saying `running`, which is
     * true, and the button is released - and if the user presses it again they get `alreadyRunning`
     * with that same run's id, so pressing it is the way back to watching. Claiming the run finished
     * would be the one thing this must not do.
     */
    public readonly pollSyncRun$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadSyncRunSuccess),
            withLatestFrom(this.store.select(selectProjectId), this.store.select(selectSyncPollTick)),
            switchMap(([{ run }, projectId, tick]) => {
                // "Never synchronised" is an empty state, not something to wait for.
                if (run == null) {
                    return EMPTY;
                }
                if (run.status !== 'pending' && run.status !== 'running') {
                    return of(closingDocumentActions.syncRunSettled({ run }));
                }
                if (projectId == null || tick >= this.intervals.maxTicks) {
                    return of(closingDocumentActions.syncRunSettled({ run }));
                }

                return timer(this.intervals.fastMs).pipe(
                    map(() => closingDocumentActions.loadSyncRun({ projectId, runId: run.id })),
                );
            }),
        ),
    );

    /**
     * A settled run reloads the list - the import created rows and moved the server-side counts, so
     * the hub cannot show the outcome without re-reading. Exactly what `reloadAfterSync$` does for
     * the synchronous pull, at the moment the asynchronous one actually finishes.
     */
    public readonly reloadAfterSyncRun$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.syncRunSettled),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([, projectId, filter, search, sort, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /** Reload after a pull, for the same reason every other mutation reloads: the counts are server-side. */
    public readonly reloadAfterSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.syncSharepointSuccess),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([, projectId, filter, search, sort, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * Changing the pill reloads: which rows exist at all is the SERVER's decision, not a local filter.
     * The search term rides along, because switching pills under an active search must keep the counts
     * narrowed to it — clicking through to the matches is the whole reason the other pills show a
     * number at all.
     */
    public readonly reloadOnFilterChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setFilter),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([{ filter }, projectId, search, sort, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * Searching reloads too — DEBOUNCED — because the search is a server query: it recomputes the
     * three pill counts over the whole closing, which is the entire reason it goes to the server at
     * all. The user types "Bilanz" and reads off Eingang (2) | Zugeordnet (5) | Nicht relevant (0),
     * then clicks the pill the matches are actually under.
     *
     * The ROWS narrow WITHOUT this effect: `selectVisibleItems` filters the loaded pill in the client,
     * so the table reacts the moment the term reaches the slice while the counts follow one round trip
     * later. That split is deliberate — the half the client can answer must not wait for the half only
     * the server can.
     *
     * `distinctUntilChanged` sits AFTER the debounce and on the TRIMMED term, so a word deleted and
     * retyped, or trailing whitespace, costs nothing. It also absorbs the repeated `setSearchTerm('')`
     * the closing shell dispatches on every tab change. The load itself is `switchMap`ed downstream,
     * so a term that keeps changing cancels its own in-flight requests rather than stacking them.
     */
    public readonly reloadOnSearchChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setSearchTerm),
            map(({ searchTerm }) => searchTerm.trim()),
            debounceTime(SEARCH_RELOAD_DEBOUNCE_MS),
            distinctUntilChanged(),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([search, projectId, filter, sort, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * A column header click reloads — the ORDER is the server's answer now.
     *
     * NOT debounced, unlike the search: a sort is one deliberate click, not a stream of keystrokes,
     * and delaying it would make the header feel broken. The load is `switchMap`ed downstream, so
     * clicking two headers quickly cancels the first request rather than racing it.
     *
     * Sorting server-side is what lets the hub order by columns the client cannot reconstruct — the
     * filename lives on another aggregate, and `account`/`assignment` are derived — and it removes the
     * second comparator that would otherwise have to agree with the server's forever.
     */
    public readonly reloadOnSortChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setSort),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([{ sort }, projectId, filter, search, agentOnly]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * REV-21901 N3 (W3) — the facet toggle reloads, the mirror of `reloadOnFilterChange$`: WHICH rows
     * the machine-assigned set contains is the SERVER's answer now (`assignedBy=agent`), not a local
     * predicate over whatever happens to be loaded. NOT debounced, like the sort: one deliberate
     * click. The facet comes from the ACTION (the value the user just set — same rule as `Set
     * Filter`'s `filter`); pill, search term and sort come from the store, because the facet lies
     * ACROSS them and toggling it must not reset any of them.
     */
    public readonly reloadOnAgentOnlyChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setAgentOnly),
            withLatestFrom(
                this.store.select(selectProjectId),
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
            ),
            mergeMap(([{ agentOnly }, projectId, filter, search, sort]) =>
                projectId == null
                    ? []
                    : [
                          closingDocumentActions.loadDocuments({
                              projectId,
                              filter,
                              search,
                              sort,
                              assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                          }),
                      ],
            ),
        ),
    );

    /**
     * The open account's documents. `switchMap`, so moving quickly between accounts cancels the
     * previous read instead of letting a slow one land after a fast one and show the wrong account's
     * evidence under the right account's header.
     */
    public readonly loadAccountDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadAccountDocuments),
            switchMap(({ projectId, accountId }) =>
                this.service.listAccountDocuments(projectId, accountId).pipe(
                    map((response) => closingDocumentActions.loadAccountDocumentsSuccess({ response })),
                    catchError(() => of(closingDocumentActions.loadAccountDocumentsFailure())),
                ),
            ),
        ),
    );

    /**
     * The cache guard for the account plan.
     *
     * "One request per closing" was the intention of the effect below, and it held for "not once per
     * picker open". It did NOT hold for "not once per page visit": the hub dispatches on mount, the
     * deep-linkable single-document route dispatches on mount, and the hub remounts on every return
     * from it — so one pass through hub → document → hub asked for the same plan three times.
     *
     * Unconditional here, unlike the account surface's list guard, and the reason is what the plan
     * depends on: it is the closing's chart, it takes no query, and it changes only when an ERP sync
     * creates account state documents. That event marks this slice stale (see the reducer), so there
     * is nothing else to compare and no staleness window to reason about.
     *
     * A request already in flight is left alone rather than restarted — `switchMap` below would cancel
     * and re-issue it for the same answer.
     */
    public readonly loadAccountsIfNeeded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadAccountsIfNeeded),
            withLatestFrom(this.store.select(selectProjectId), this.store.select(selectAccountsStatus)),
            filter(
                ([{ projectId }, loadedFor, status]) =>
                    !(projectId === loadedFor && (status === 'loaded' || status === 'loading')),
            ),
            map(([{ projectId }]) => closingDocumentActions.loadAccounts({ projectId })),
        ),
    );

    /** The account plan. One request per closing — both pickers and both surfaces read the same list. */
    public readonly loadAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadAccounts),
            switchMap(({ projectId }) =>
                this.service.listAccounts(projectId).pipe(
                    map((accounts) => closingDocumentActions.loadAccountsSuccess({ accounts })),
                    catchError(() => of(closingDocumentActions.loadAccountsFailure())),
                ),
            ),
        ),
    );

    /**
     * File a document, then reload.
     *
     * The reload is not optional: filing moves the document from Eingang to Zugeordnet, so BOTH pill
     * counts change and only the server knows the new numbers. The row also has to pick up its account
     * chip, whose label the server composes.
     *
     * `mergeMap`, not `switchMap`: two documents can legitimately be filed in quick succession and the
     * second must not cancel the first — these are WRITES, and a cancelled write still happened
     * server-side while the UI would show it as if it had not.
     */
    public readonly assignDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.assignDocument),
            withLatestFrom(
                this.store.select(selectActiveFilter),
                this.store.select(selectSearchTerm),
                this.store.select(selectDocumentSort),
                this.store.select(selectAgentOnly),
            ),
            mergeMap(([{ projectId, documentId, request }, filter, search, sort, agentOnly]) =>
                this.service.setAssignment(projectId, documentId, request).pipe(
                    // TWO actions, because filing is reachable from BOTH surfaces and each needs a
                    // different thing refreshed. The hub needs the LIST (the row moved pills and the
                    // counts changed); the single-document page needs THE DOCUMENT — its sidebar is
                    // rendered from `state.document`, which a list reload does not touch, so filing
                    // there left the picker showing the pre-filing state until a manual reload.
                    //
                    // The updated document comes back in the response, so refreshing it costs nothing.
                    mergeMap((document) => [
                        closingDocumentActions.loadDocumentSuccess({ document }),
                        closingDocumentActions.loadDocuments({
                            projectId,
                            filter,
                            search,
                            sort,
                            assignedBy: ClosingDocumentEffects.toAssignedBy(agentOnly),
                        }),
                        // REV-22366 — the row waits for that list, then leaves its loading state.
                        closingDocumentActions.rowWriteSucceeded({ documentId }),
                    ]),
                    catchError(() =>
                        of(
                            closingDocumentActions.rowWriteFailed({ documentId }),
                            closingDocumentActions.loadDocumentsFailure(),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Un-file, then reload — the counts move back the other way.
     *
     * #84/F5 — a failed unfiling reports `proposalFailure`, not `loadDocumentsFailure`: this is the
     * RETRACTION path of a machine assignment (N2), and the same untruth applied here — "documents
     * could not be loaded" — would take the sidebar away from a user who just clicked "Zuordnung
     * entfernen" on a document that loaded fine a moment ago.
     *
     * #84/F9/F10 (Sicherheitspruefung, Fall 18) — `groupBy(documentId)` + inner `exhaustMap`: writes
     * for DIFFERENT documents still run CONCURRENTLY (the outer `mergeMap` over groups), but a
     * SECOND un-file for the SAME document while the first is still in flight is a no-op — the group
     * simply ignores it, exactly the "one write per document while it runs" requirement. See
     * `acceptProposal$` for the full reasoning; this shares it.
     */
    public readonly unassignDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.unassignDocument),
            groupBy(({ documentId }) => documentId),
            mergeMap((group$) =>
                group$.pipe(
                    withLatestFrom(
                        this.store.select(selectActiveFilter),
                        this.store.select(selectSearchTerm),
                        this.store.select(selectDocumentSort),
                        this.store.select(selectAgentOnly),
                    ),
                    exhaustMap(([{ projectId, documentId }, filter, search, sort, agentOnly]) =>
                        this.service.clearAssignment(projectId, documentId).pipe(
                            mergeMap((document) =>
                                this.refreshAfterWrite(document, documentId, projectId, filter, search, sort, agentOnly),
                            ),
                            catchError(() => of(closingDocumentActions.proposalFailure({ documentId }))),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * #84/F1/F3 — accept or reject the proposal #57's auto-assignment produced.
     *
     * `groupBy(documentId)` + inner `exhaustMap`, NOT a flat `mergeMap` — the SECURITY REVIEW found
     * the flat version to be a real double-write path (Sicherheitspruefung, Fall 18/F9): accept A,
     * then accept B, then accept A again. A flat `mergeMap` never rejects the third click — nothing
     * about it distinguishes "a write for A is already running" from "a write for A never started".
     * `groupBy` splits the action stream into ONE sub-stream PER DOCUMENT; the outer `mergeMap`
     * subscribes to every group CONCURRENTLY (so two DIFFERENT documents still both reach the server
     * without waiting on each other — the ORIGINAL reason this effect used `mergeMap`, still true and
     * still measured by integration case 17); the inner `exhaustMap` ignores a new emission on a
     * group that is already mid-request, which is exactly "one write per document while it runs" —
     * not "one write overall" (that would be `switchMap`/`exhaustMap` at the OUTER level, which is
     * wrong for the reason the class doc always gave: it would race two DIFFERENT documents against
     * each other). This cannot be a `disabled` attribute or a store-state `filter` before the write:
     * by the time an effect observes an action, the reducer has ALREADY run for it, so a state check
     * here would see documentId as "pending" from the SAME dispatch it is supposed to admit. The
     * guard has to live in the write's own concurrency operator, which is what this is.
     *
     * F2 — success answers with the UPDATED document and refreshes BOTH surfaces exactly like
     * `assignDocument$`: the sidebar via `loadDocumentSuccess` (from the write's own response, which
     * cannot fail) and the row (pill, counts) via a LIST reload — see `refreshAfterWrite`.
     *
     * F5 — the WRITE's own failure reports `proposalFailure`, never `loadDocumentsFailure`: that
     * action means "the list/document could not be LOADED" and takes the whole surface away from the
     * user, precisely where they are working.
     */
    public readonly acceptProposal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.acceptProposal),
            groupBy(({ documentId }) => documentId),
            mergeMap((group$) =>
                group$.pipe(
                    withLatestFrom(
                        this.store.select(selectActiveFilter),
                        this.store.select(selectSearchTerm),
                        this.store.select(selectDocumentSort),
                        this.store.select(selectAgentOnly),
                    ),
                    exhaustMap(([{ projectId, documentId, accountId }, filter, search, sort, agentOnly]) =>
                        this.service.acceptProposal(projectId, documentId, accountId).pipe(
                            mergeMap((document) =>
                                this.refreshAfterWrite(document, documentId, projectId, filter, search, sort, agentOnly),
                            ),
                            catchError(() => of(closingDocumentActions.proposalFailure({ documentId }))),
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Same shape as `acceptProposal$` — see there for the `groupBy`/`exhaustMap`/F2/F5/F9 reasoning. */
    public readonly rejectProposal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.rejectProposal),
            groupBy(({ documentId }) => documentId),
            mergeMap((group$) =>
                group$.pipe(
                    withLatestFrom(
                        this.store.select(selectActiveFilter),
                        this.store.select(selectSearchTerm),
                        this.store.select(selectDocumentSort),
                        this.store.select(selectAgentOnly),
                    ),
                    exhaustMap(([{ projectId, documentId }, filter, search, sort, agentOnly]) =>
                        this.service.rejectProposal(projectId, documentId).pipe(
                            mergeMap((document) =>
                                this.refreshAfterWrite(document, documentId, projectId, filter, search, sort, agentOnly),
                            ),
                            catchError(() => of(closingDocumentActions.proposalFailure({ documentId }))),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Dauerakte, then re-read THE DOCUMENT — not the list.
     *
     * Validity changes no pill and no count: it decides what the NEXT closing inherits (ADR-0009 §5).
     * Reloading the whole list for it would be a request that changes nothing on screen, and on the
     * single-document route it is not even the list the user is looking at.
     */
    public readonly setDocumentValidity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.setDocumentValidity),
            mergeMap(({ projectId, documentId, validity }) =>
                this.service.setValidity(projectId, documentId, validity).pipe(
                    mergeMap((document) => [
                        closingDocumentActions.loadDocumentSuccess({ document }),
                        closingDocumentActions.documentWriteSucceeded({ documentId }),
                    ]),
                    // A refused write keeps the page and says so; it is not a failed load.
                    catchError(() => of(closingDocumentActions.rowWriteFailed({ documentId }))),
                ),
            ),
        ),
    );

    /**
     * #84/F14 (Sicherheitspruefung, Fall 19) — what a successful accept/reject/un-file does next.
     *
     * TWO refreshes, and they do NOT share a failure mode:
     *   · the SIDEBAR, from the write's OWN response (`document`) — this cannot fail, it is already
     *     in hand;
     *   · the LIST, a fresh round trip, because the pill counts are server-side and the row has to
     *     pick up its new chip.
     *
     * A failed WRITE and a failed LIST-REFRESH used to collapse onto the same `loadDocumentsFailure`
     * — "the documents could not be loaded" — which is doubly wrong here: the write SUCCEEDED (a
     * 200 came back), and the sentence takes the table away from a user who just watched their own
     * click land. The refresh's own failure reports on the ROW instead (`proposalFailure`), the same
     * action and the same on-screen spot a failed WRITE already uses — not a new untruth, the
     * existing one, on the row it is actually about.
     */
    private refreshAfterWrite(
        document: IClosingDocumentDetail,
        documentId: string,
        projectId: string,
        filter: DocumentFilterKey,
        search: string,
        sort: DocumentSort | null,
        agentOnly: boolean,
    ): Observable<Action> {
        return merge(
            of(closingDocumentActions.loadDocumentSuccess({ document })),
            this.service
                .listDocuments(projectId, filter, search, sort, ClosingDocumentEffects.toAssignedBy(agentOnly))
                .pipe(
                    map((response) => closingDocumentActions.loadDocumentsSuccess({ response })),
                    catchError(() => of(closingDocumentActions.proposalFailure({ documentId }))),
                ),
        );
    }

    /**
     * Turn a failed upload into something the modal can show per file.
     *
     * 415 is the ADR's PDF gate and by far the likeliest failure, so it gets its own message rather
     * than a generic one — "this file is not a PDF" is actionable, "upload failed" is not.
     */
    private static toFailureReason(error: unknown): string {
        const status = (error as { status?: number })?.status;
        if (status === HTTP_UNSUPPORTED_MEDIA_TYPE) {
            return 'abcp_upload_modal_error_not_pdf';
        }
        if (status === HTTP_PAYLOAD_TOO_LARGE) {
            return 'abcp_upload_modal_error_too_large';
        }
        return 'abcp_upload_modal_error_generic';
    }
}
